<template>
  <section
    class="kids-hero-slider"
    aria-roledescription="carousel"
    aria-label="Kids sports development stages"
    @keydown.left.prevent="goPrev"
    @keydown.right.prevent="goNext"
  >
    <div
      ref="viewport"
      class="kids-hero-slider__viewport"
      @mouseenter="hovering = true"
      @mouseleave="hovering = false"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
    >
      <article
        v-for="(slide, index) in slides"
        :key="slide.id"
        class="kids-hero-slider__slide"
        :class="{
          'kids-hero-slider__slide--active': index === currentIndex,
          'kids-hero-slider__slide--leaving': index === leavingIndex,
        }"
        :aria-hidden="index !== currentIndex"
      >
        <img
          :src="slide.image"
          :alt="slide.alt"
          class="kids-hero-slider__image"
          :loading="index === 0 ? 'eager' : 'lazy'"
          :fetchpriority="index === 0 ? 'high' : 'auto'"
          decoding="async"
        />
        <div class="kids-hero-slider__shade" aria-hidden="true" />
        <div class="kids-hero-slider__content">
          <span class="kids-hero-slider__stage">Stage {{ slide.stage }}</span>
          <h2 class="kids-hero-slider__title">{{ slide.title }}</h2>
          <p class="kids-hero-slider__subtitle">{{ slide.subtitle }}</p>
        </div>
      </article>

      <div class="kids-hero-slider__top-bar">
        <span class="kids-hero-slider__counter" aria-live="polite">
          {{ currentIndex + 1 }} / {{ slides.length }}
        </span>
        <button
          type="button"
          class="kids-hero-slider__pause"
          :aria-pressed="!isPlaying"
          :aria-label="isPlaying ? 'Pause slideshow' : 'Play slideshow'"
          @click="togglePlayback"
        >
          <i class="fa-solid" :class="isPlaying ? 'fa-pause' : 'fa-play'" aria-hidden="true" />
          <span>{{ isPlaying ? "Pause" : "Play" }}</span>
        </button>
      </div>

      <button
        type="button"
        class="kids-hero-slider__arrow kids-hero-slider__arrow--prev"
        aria-label="Previous slide"
        @click="goPrev"
      >
        <i class="fa-solid fa-chevron-left" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="kids-hero-slider__arrow kids-hero-slider__arrow--next"
        aria-label="Next slide"
        @click="goNext"
      >
        <i class="fa-solid fa-chevron-right" aria-hidden="true" />
      </button>
    </div>

    <div class="kids-hero-slider__footer">
      <div class="kids-hero-slider__dots" role="tablist" aria-label="Choose development stage">
        <button
          v-for="(slide, index) in slides"
          :key="`dot-${slide.id}`"
          type="button"
          role="tab"
          class="kids-hero-slider__dot"
          :class="{ 'kids-hero-slider__dot--active': index === currentIndex }"
          :aria-selected="index === currentIndex"
          :aria-label="`Go to ${slide.title}`"
          @click="goTo(index)"
        >
          <span class="kids-hero-slider__dot-label">{{ slide.title }}</span>
        </button>
      </div>

      <div
        class="kids-hero-slider__progress"
        role="progressbar"
        :aria-valuenow="progressPercent"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="`Slide ${currentIndex + 1} progress`"
      >
        <span
          :key="progressKey"
          class="kids-hero-slider__progress-fill"
          :class="{ 'kids-hero-slider__progress-fill--paused': !isPlaying }"
          :style="{ animationDuration: `${intervalMs}ms` }"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { KIDS_HERO_SLIDES } from "../constants";

export default {
  name: "KidsHeroSlider",

  props: {
    slides: {
      type: Array,
      default: () => KIDS_HERO_SLIDES,
    },
    intervalMs: {
      type: Number,
      default: 5500,
    },
  },

  data() {
    return {
      currentIndex: 0,
      leavingIndex: null,
      isPlaying: true,
      hovering: false,
      timer: null,
      progressKey: 0,
      touchStartX: 0,
      reducedMotion: false,
    };
  },

  computed: {
    progressPercent() {
      if (!this.slides.length) return 0;
      return Math.round(((this.currentIndex + 1) / this.slides.length) * 100);
    },
    shouldAutoplay() {
      return this.isPlaying && !this.hovering && !this.reducedMotion;
    },
  },

  watch: {
    shouldAutoplay: {
      immediate: true,
      handler(active) {
        if (active) this.startTimer();
        else this.stopTimer();
      },
    },
    currentIndex() {
      this.progressKey += 1;
    },
  },

  mounted() {
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (this.reducedMotion) this.isPlaying = false;
  },

  beforeUnmount() {
    this.stopTimer();
  },

  methods: {
    startTimer() {
      this.stopTimer();
      this.timer = window.setInterval(() => {
        this.goNext();
      }, this.intervalMs);
    },

    stopTimer() {
      if (this.timer != null) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    },

    togglePlayback() {
      this.isPlaying = !this.isPlaying;
    },

    goTo(index) {
      if (index === this.currentIndex || index < 0 || index >= this.slides.length) return;
      this.leavingIndex = this.currentIndex;
      this.currentIndex = index;
      window.setTimeout(() => {
        this.leavingIndex = null;
      }, 700);
    },

    goNext() {
      this.goTo((this.currentIndex + 1) % this.slides.length);
    },

    goPrev() {
      this.goTo((this.currentIndex - 1 + this.slides.length) % this.slides.length);
    },

    onTouchStart(event) {
      this.touchStartX = event.changedTouches?.[0]?.clientX ?? 0;
    },

    onTouchEnd(event) {
      const endX = event.changedTouches?.[0]?.clientX ?? 0;
      const delta = endX - this.touchStartX;
      if (Math.abs(delta) < 48) return;
      if (delta < 0) this.goNext();
      else this.goPrev();
    },
  },
};
</script>

<style scoped>
.kids-hero-slider {
  width: 100%;
  max-width: none;
  margin: 0;
}

.kids-hero-slider__viewport {
  position: relative;
  border-radius: 0;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  aspect-ratio: 21 / 9;
  min-height: clamp(200px, 38vh, 560px);
  max-height: min(72vh, 720px);
  background: var(--background-color2);
  border: none;
  border-top: 1px solid color-mix(in srgb, var(--border-color-hover) 28%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--border-color-hover) 28%, transparent);
  box-shadow: none;
}

.kids-hero-slider__slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transform: scale(1.04);
  transition:
    opacity 0.75s cubic-bezier(0.4, 0, 0.2, 1),
    transform 1.1s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  z-index: 1;
}

.kids-hero-slider__slide--active {
  opacity: 1;
  transform: scale(1);
  pointer-events: auto;
  z-index: 2;
}

.kids-hero-slider__slide--leaving {
  opacity: 0;
  transform: scale(1.02);
  z-index: 1;
}

.kids-hero-slider__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.kids-hero-slider__shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    color-mix(in srgb, var(--background-color2) 72%, transparent) 0%,
    color-mix(in srgb, var(--background-color2) 35%, transparent) 42%,
    transparent 68%
  );
  pointer-events: none;
}

.kids-hero-slider__content {
  position: absolute;
  left: clamp(1rem, 4vw, 2.25rem);
  right: clamp(1rem, 4vw, 2.25rem);
  bottom: clamp(1rem, 4vw, 2rem);
  max-width: 34ch;
  z-index: 3;
  animation: kidsSlideContentIn 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.kids-hero-slider__slide--active .kids-hero-slider__content {
  animation: kidsSlideContentIn 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes kidsSlideContentIn {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.kids-hero-slider__stage {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.55rem;
  padding: 0.28rem 0.7rem;
  border-radius: 999px;
  font-size: clamp(0.62rem, 1.8vw, 0.72rem);
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--background-color1);
  background: var(--border-color-hover);
}

.kids-hero-slider__title {
  margin: 0 0 0.45rem;
  font-size: clamp(1.15rem, 3.8vw, 2rem);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.02em;
  color: var(--text-color1);
  text-wrap: balance;
}

.kids-hero-slider__subtitle {
  margin: 0;
  font-size: clamp(0.78rem, 2.2vw, 0.95rem);
  line-height: 1.5;
  color: var(--text-color2);
}

.kids-hero-slider__top-bar {
  position: absolute;
  top: clamp(0.65rem, 2vw, 1rem);
  right: clamp(0.65rem, 2vw, 1rem);
  left: clamp(0.65rem, 2vw, 1rem);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  z-index: 4;
}

.kids-hero-slider__counter {
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--text-color1);
  background: color-mix(in srgb, var(--background-color2) 55%, transparent);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 25%, transparent);
  backdrop-filter: blur(8px);
}

.kids-hero-slider__pause {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 36px;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 35%, transparent);
  background: color-mix(in srgb, var(--background-color2) 70%, transparent);
  color: var(--text-color1);
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  backdrop-filter: blur(8px);
  transition: border-color 0.2s ease, transform 0.2s ease, background 0.2s ease;
}

.kids-hero-slider__pause:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-1px);
}

.kids-hero-slider__pause i {
  color: var(--border-color-hover);
  font-size: 0.72rem;
}

.kids-hero-slider__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  width: clamp(36px, 8vw, 44px);
  height: clamp(36px, 8vw, 44px);
  border-radius: 50%;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 30%, transparent);
  background: color-mix(in srgb, var(--background-color2) 65%, transparent);
  color: var(--text-color1);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  opacity: 0.85;
  transition: opacity 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.kids-hero-slider__arrow:hover {
  opacity: 1;
  border-color: var(--border-color-hover);
  transform: translateY(-50%) scale(1.05);
}

.kids-hero-slider__arrow--prev {
  left: clamp(0.5rem, 2vw, 0.85rem);
}

.kids-hero-slider__arrow--next {
  right: clamp(0.5rem, 2vw, 0.85rem);
}

.kids-hero-slider__footer {
  margin-top: clamp(0.75rem, 2vw, 1rem);
  display: grid;
  gap: 0.65rem;
  width: 100%;
  max-width: 100%;
  padding: 0 clamp(0.875rem, 3.5vw, 2.75rem);
  box-sizing: border-box;
}

.kids-hero-slider__dots {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
}

.kids-hero-slider__dot {
  position: relative;
  min-height: 44px;
  padding: 0.55rem 0.65rem;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 18%, transparent);
  background: var(--background-color1);
  color: var(--text-color2);
  font-family: inherit;
  font-size: clamp(0.62rem, 1.8vw, 0.75rem);
  font-weight: 600;
  line-height: 1.25;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.25s ease,
    background 0.25s ease,
    color 0.25s ease,
    transform 0.25s ease,
    box-shadow 0.25s ease;
}

.kids-hero-slider__dot:hover {
  border-color: var(--border-color-hover);
  transform: translateY(-2px);
}

.kids-hero-slider__dot--active {
  border-color: var(--border-color-hover);
  background: color-mix(in srgb, var(--border-color-hover) 12%, var(--background-color1));
  color: var(--text-color1);
  box-shadow: 0 8px 22px color-mix(in srgb, var(--border-color-hover) 12%, transparent);
}

.kids-hero-slider__dot-label {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.kids-hero-slider__progress {
  height: 4px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--border-color-hover) 12%, var(--background-color1));
  overflow: hidden;
}

.kids-hero-slider__progress-fill {
  display: block;
  height: 100%;
  width: 0;
  border-radius: inherit;
  background: linear-gradient(
    90deg,
    var(--border-color-hover),
    color-mix(in srgb, var(--border-color-hover) 65%, var(--text-color1))
  );
  animation: kidsProgress linear forwards;
}

.kids-hero-slider__progress-fill--paused {
  animation-play-state: paused;
}

@keyframes kidsProgress {
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
}

@media (max-width: 1024px) {
  .kids-hero-slider__viewport {
    aspect-ratio: 16 / 9;
    min-height: clamp(200px, 42vh, 480px);
    max-height: 65vh;
  }
}

@media (max-width: 768px) {
  .kids-hero-slider__viewport {
    aspect-ratio: 4 / 3;
    min-height: clamp(180px, 48vw, 380px);
    max-height: 58vh;
  }

  .kids-hero-slider__content {
    max-width: 100%;
  }

  .kids-hero-slider__title {
    font-size: clamp(1rem, 4.5vw, 1.35rem);
  }

  .kids-hero-slider__subtitle {
    font-size: 0.75rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .kids-hero-slider__pause span {
    display: none;
  }

  .kids-hero-slider__pause {
    min-width: 36px;
    justify-content: center;
    padding: 0.4rem;
  }

  .kids-hero-slider__dots {
    grid-template-columns: 1fr;
  }

  .kids-hero-slider__dot {
    min-height: 38px;
    font-size: 0.68rem;
    padding: 0.45rem 0.55rem;
  }

  .kids-hero-slider__arrow {
    width: 34px;
    height: 34px;
  }
}

@media (max-width: 480px) {
  .kids-hero-slider__viewport {
    aspect-ratio: 3 / 2;
    min-height: clamp(150px, 45vw, 280px);
    max-height: 45vh;
  }

  .kids-hero-slider__content {
    left: 0.75rem;
    right: 0.75rem;
    bottom: 0.75rem;
  }

  .kids-hero-slider__title {
    font-size: clamp(0.92rem, 4.8vw, 1.15rem);
  }

  .kids-hero-slider__stage {
    font-size: 0.58rem;
    padding: 0.22rem 0.55rem;
  }

  .kids-hero-slider__footer {
    padding-inline: clamp(0.65rem, 4vw, 1rem);
  }

  .kids-hero-slider__arrow--prev {
    left: 0.35rem;
  }

  .kids-hero-slider__arrow--next {
    right: 0.35rem;
  }
}

@media (max-width: 360px) {
  .kids-hero-slider__viewport {
    min-height: clamp(140px, 48vw, 240px);
    max-height: 40vh;
  }

  .kids-hero-slider__counter {
    font-size: 0.65rem;
    padding: 0.28rem 0.5rem;
  }

  .kids-hero-slider__dot {
    min-height: 36px;
    font-size: 0.62rem;
  }
}

@media (max-height: 480px) and (orientation: landscape) {
  .kids-hero-slider__viewport {
    aspect-ratio: 21 / 9;
    min-height: unset;
    max-height: 70vh;
    height: clamp(120px, 55vh, 280px);
  }

  .kids-hero-slider__content {
    bottom: 0.5rem;
  }

  .kids-hero-slider__subtitle {
    -webkit-line-clamp: 1;
  }

  .kids-hero-slider__dots {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 1536px) {
  .kids-hero-slider__viewport {
    min-height: clamp(320px, 40vh, 640px);
  }

  .kids-hero-slider__footer {
    padding-inline: clamp(2rem, 4vw, 4rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  .kids-hero-slider__slide,
  .kids-hero-slider__arrow,
  .kids-hero-slider__dot,
  .kids-hero-slider__pause {
    transition: none;
  }

  .kids-hero-slider__progress-fill {
    animation: none;
    width: 100%;
    opacity: 0.35;
  }
}
</style>
